import { useCallback } from 'react';
import {
  applyTransformations,
  validateField,
} from '../../utils/fieldValidators';
import { useLanguage } from '../../context/LanguageContext';
import { isJsonField } from '../../Templates/Table/TableTemplate/tableFieldRenderer';
import { parseValidationRules } from '../../utils/parseValidationRules';

interface UseRowValidationProps {
  fields: Array<{
    name: string;
    file_type?: string;
    type?: string;
    validation?: any;
  }>;
}

export function useRowValidation({ fields }: UseRowValidationProps) {
  const { t } = useLanguage();

  const tFn = useCallback(
    (key: string, replacements?: Record<string, string | number>) => {
      let text = t(key);

      if (replacements) {
        Object.entries(replacements).forEach(([k, v]) => {
          text = text.replace(new RegExp(`{{${k}}}`, 'g'), String(v));
          text = text.replace(new RegExp(`{${k}}`, 'g'), String(v));
        });
      }

      return text;
    },
    [t]
  );

  const validateRow = useCallback(
    (rowData: Record<string, any>) => {
      const errors: Record<string, string> = {};

      for (const field of fields) {
        const value = rowData[field.name];

        // JSON validation
        if (isJsonField(field)) {
          if (value && typeof value === 'string' && value.trim() !== '') {
            try {
              JSON.parse(value);
            } catch {
              errors[field.name] = t('invalidJson');
              continue;
            }
          }
        }

        const validationRules = parseValidationRules(field.validation);

        if (validationRules?.length) {
          const result = validateField(
            value,
            validationRules,
            field.name,
            tFn
          );

          if (!result.isValid && result.error) {
            errors[field.name] = result.error;
          }
        }
      }

      return {
        isValid: Object.keys(errors).length === 0,
        errors,
      };
    },
    [fields, t, tFn]
  );

  const validateSingleField = useCallback(
    (fieldName: string, value: any) => {
      const field = fields.find(f => f.name === fieldName);
      if (!field) return { isValid: true };

      if (isJsonField(field)) {
        if (value && typeof value === 'string' && value.trim() !== '') {
          try {
            JSON.parse(value);
          } catch {
            return {
              isValid: false,
              error: t('invalidJson'),
            };
          }
        }
      }

      const validationRules = parseValidationRules(field.validation);
      if (!validationRules?.length) return { isValid: true };

      return validateField(value, validationRules, fieldName, tFn);
    },
    [fields, t, tFn]
  );

  const transformValue = useCallback(
    (
      fieldName: string,
      value: any,
      isBeforeSave: boolean = false
    ) => {
      const field = fields.find(f => f.name === fieldName);
      if (!field) return value;

      const validationRules = parseValidationRules(field.validation);
      if (!validationRules?.length) return value;

      return applyTransformations(
        value,
        validationRules,
        field.file_type || field.type,
        isBeforeSave
      );
    },
    [fields]
  );

  const isRowEmpty = useCallback(
    (rowData: Record<string, any>) => {
      return Object.entries(rowData).every(([key, value]) => {
        if (key.toLowerCase() === 'id') return true;

        const field = fields.find(f => f.name === key);

        if (
          field &&
          (field.file_type === 'boolean' || field.type === 'boolean')
        ) {
          if (value === false) return true;
        }

        if (value === '' || value == null) return true;

        if (field && isJsonField(field) && typeof value === 'string') {
          const trimmed = value.trim();
          if (trimmed === '{}' || trimmed === '[]' || trimmed === '') {
            return true;
          }
        }

        return false;
      });
    },
    [fields]
  );

  return {
    validateRow,
    validateSingleField,
    transformValue,
    isRowEmpty,
  };
}